import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { CustomerProfileModal } from '@/components/CustomerProfileModal';
import { formatPhoneNumber } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { CustomToast } from '@/components/ui/CustomToast';
import { useApp } from '@/contexts/AppContext';

// Define Customer type
interface Customer {
  id: string;
  phoneNumber: string;
  name?: string;
  email?: string;
  visits: any[];
  redeemedRewards: any[];
}

interface StaffNotification {
  id: string;
  message: string;
  timestamp: Date;
  type: 'scan' | 'visit' | 'reward';
  customerId: string;
}

interface StaffNotificationContextType {
  notifications: StaffNotification[];
  addNotification: (notification: Omit<StaffNotification, 'id' | 'timestamp'>) => void;
  clearNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const StaffNotificationContext = createContext<StaffNotificationContextType | undefined>(undefined);

export const useStaffNotifications = () => {
  const context = useContext(StaffNotificationContext);
  if (!context) {
    throw new Error('useStaffNotifications must be used within a StaffNotificationProvider');
  }
  return context;
};

// Inner provider component that uses Router hooks
const StaffNotificationProviderWithRouter: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { currentUser } = useApp();
  const [notifications, setNotifications] = useState<StaffNotification[]>([]);
  const [activeCustomerId, setActiveCustomerId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Use a ref to track processed notification IDs
  const processedNotificationIdsRef = useRef<Set<string>>(new Set());
  
  // Check if currently on the scan page
  const isOnScanPage = location.pathname === '/scanqr' || location.pathname === '/scan';

  // Function to play notification sound
  const playNotificationSound = () => {
    if (audioRef.current) {
      // Clone and play to allow overlapping sounds
      const soundClone = audioRef.current.cloneNode() as HTMLAudioElement;
      soundClone.play().catch(error => {
        console.log("[StaffNotificationContext] Audio playback failed:", error);
      });
    }
  };

  useEffect(() => {
    // Create audio element for notification sound
    const audio = new Audio('/notification.mp3');
    audio.preload = 'auto';
    audioRef.current = audio;

    return () => {
      // Clean up audio element
      if (audioRef.current) {
        audioRef.current = null;
      }
    };
  }, []);

  // Set up direct database polling for notifications - works across all tabs
  // Only poll when user is logged in
  useEffect(() => {
    // If no user is logged in, don't set up polling
    if (!currentUser) {
      console.log("[StaffNotificationContext] No user logged in, skipping notification polling");
      return;
    }
    
    console.log("[StaffNotificationContext] Setting up notification polling for logged-in user");
    
    // Keep track of when we started polling
    const startTime = new Date();
    console.log(`[StaffNotificationContext] Starting polling at: ${startTime.toISOString()}`);
    
    // Function to check for new notifications
    const checkForNotifications = async () => {
      try {
        // Use RPC function to get notifications (bypasses RLS)
        const { data, error } = await supabase.rpc('get_staff_notifications', {
          limit_count: 5
        });
        
        if (error) {
          console.error("[StaffNotificationContext] Error fetching notifications:", error);
          return;
        }
        
        if (!data || data.length === 0) return;
        
        // Process each notification that we haven't seen before
        for (const notification of data) {
          // Skip if we've already processed this notification
          if (processedNotificationIdsRef.current.has(notification.id)) continue;
          
          // Check if this is a new notification (created after we started polling)
          const notificationTime = new Date(notification.created_at);
          const isNewNotification = notificationTime > startTime;
          
          // Only process notifications that were created after we started polling
          if (!isNewNotification) {
            // Just mark old notifications as processed without showing them
            processedNotificationIdsRef.current.add(notification.id);
            console.log(`[StaffNotificationContext] Skipping old notification from ${notificationTime.toLocaleTimeString()}`);
            continue;
          }
          
          console.log("[StaffNotificationContext] New notification found:", notification);
          
          // Mark as processed
          processedNotificationIdsRef.current.add(notification.id);
          
          // Skip if not a scan notification
          if (notification.type !== 'scan') continue;
          
          const customerId = notification.customer_id;
          
          // Get customer details from database
          const { data: customerData } = await supabase
            .from('customers')
            .select('*')
            .eq('id', customerId)
            .single();
          
          if (!customerData) {
            console.log("[StaffNotificationContext] Customer not found in database");
            continue;
          }
          
          // Create customer object
          const customer: Customer = {
            id: customerData.id,
            phoneNumber: customerData.phone_number,
            name: customerData.name,
            email: customerData.email,
            visits: [],
            redeemedRewards: []
          };
          
          // Process the notification
          processCustomerNotification(customer);
        }
      } catch (error) {
        console.error("[StaffNotificationContext] Error checking for notifications:", error);
      }
    };
    
    // Check immediately
    checkForNotifications();
    
    // Set up interval to check every 3 seconds
    const intervalId = setInterval(checkForNotifications, 3000);
    
    // Cleanup
    return () => {
      clearInterval(intervalId);
    };
  }, [currentUser]); // Add currentUser as a dependency

  // Function to process customer notification
  const processCustomerNotification = (customer: Customer) => {
    console.log("[StaffNotificationContext] Processing notification for customer:", customer.name || customer.phoneNumber);
    
    // Get customer name for display
    const customerName = customer.name || formatPhoneNumber(customer.phoneNumber);
    
    // Add notification
    addNotification({
      type: 'scan',
      customerId: customer.id,
      message: `Customer ${customerName} has scanned their QR code`,
    });
    
    // Only show customer profile modal if not on the customer scan page
    if (!isOnScanPage) {
      console.log(`[StaffNotificationContext] Not on scan page, showing modal for customer ${customer.id}`);
      // Set a slight delay to ensure the component updates properly
      setTimeout(() => {
        setActiveCustomerId(customer.id);
        
        // Only play notification sound if not on scan page
        playNotificationSound();
        
        // Only show toast notification if not on scan page
        toast.success(
          <CustomToast 
            title={customerName} 
            message="Just checked in!" 
          />, 
          {
            duration: 5000
          }
        );
      }, 100);
    } else {
      console.log("[StaffNotificationContext] On scan page - not showing customer profile modal, sound, or toast");
    }
  };

  // Clear activeCustomerId when current user changes (logout)
  useEffect(() => {
    if (!currentUser) {
      setActiveCustomerId(null);
    }
  }, [currentUser]);

  useEffect(() => {
    // Only add event listeners if a user is logged in
    if (!currentUser) return;
    
    const handleCustomerScan = (event: CustomEvent<{ customerId: string; customerName?: string; phoneNumber: string }>) => {
      const { customerId, customerName, phoneNumber } = event.detail;
      
      // Add notification
      addNotification({
        type: 'scan',
        customerId,
        message: `Customer ${customerName || formatPhoneNumber(phoneNumber)} has scanned their QR code`,
      });

      // Only show customer profile modal if not on the customer scan page
      if (!isOnScanPage) {
        console.log(`[StaffNotificationContext] Event: Not on scan page, showing modal for customer ${customerId}`);
        
        // Add a slight delay to ensure state updates properly
        setTimeout(() => {
          setActiveCustomerId(customerId);
          
          // Only play notification sound if not on scan page
          playNotificationSound();
          
          // Only show toast notification if not on scan page
          const displayName = customerName || formatPhoneNumber(phoneNumber);
          toast.success(
            <CustomToast 
              title={displayName} 
              message="Just checked in!" 
            />, 
            {
              duration: 5000
            }
          );
        }, 100);
      } else {
        console.log("[StaffNotificationContext] Event: On scan page - not showing customer profile modal, sound, or toast");
      }
    };

    // Add event listener
    window.addEventListener('customerScan', handleCustomerScan as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener('customerScan', handleCustomerScan as EventListener);
    };
  }, [isOnScanPage, currentUser]); // Add currentUser as a dependency

  const addNotification = (notification: Omit<StaffNotification, 'id' | 'timestamp'>) => {
    const newNotification: StaffNotification = {
      ...notification,
      id: crypto.randomUUID(),
      timestamp: new Date(),
    };
    setNotifications(prev => [newNotification, ...prev]);
  };

  const clearNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  const handleCloseModal = () => {
    setActiveCustomerId(null);
  };

  // For debugging
  useEffect(() => {
    if (activeCustomerId) {
      console.log(`[StaffNotificationContext] Modal should be showing for customer: ${activeCustomerId}`);
    }
  }, [activeCustomerId]);

  return (
    <StaffNotificationContext.Provider
      value={{
        notifications,
        addNotification,
        clearNotification,
        clearAllNotifications,
      }}
    >
      {children}
      {/* Render the CustomerProfileModal if we have an active customer ID, we're not on the /scanqr page, and user is logged in */}
      {activeCustomerId && !isOnScanPage && currentUser && (
        <CustomerProfileModal
          customerId={activeCustomerId}
          onClose={handleCloseModal}
        />
      )}
      {/* Add audio element for notification sound */}
      <audio ref={audioRef} src="/notification.mp3" preload="auto" />
    </StaffNotificationContext.Provider>
  );
};

// Outer provider component
export const StaffNotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <StaffNotificationProviderWithRouter>{children}</StaffNotificationProviderWithRouter>;
}; 